import "core-js/modules/web.dom-collections.iterator.js";
import DOMPurify from "dompurify";
import { useEffect, useRef, useState } from "react";
import ReactDOM from "react-dom";
import { StyleSheetManager, ThemeProvider } from "styled-components";
import { terriaTheme } from "../../../../StandardUserInterface";
import { useViewState } from "../../../../Context";
import { DistanceLegend } from "../../../BottomBar/DistanceLegend";
import { buildShareLink, buildShortShareLink, canShorten } from "../BuildShareLink";
import PrintDatasets from "./PrintDatasets";
import PrintSource from "./PrintSource";
import PrintViewButtons from "./PrintViewButtons";
import PrintViewMap from "./PrintViewMap";
import PrintWorkbench from "./PrintWorkbench";
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
const PRINT_MAP_WIDTH = 1000;
const styles = "\n    .tjs-_base__list-reset {\n        list-style: none;\n        padding-left: 0;\n        margin: 0;\n    }\n\n    .mapContainer {\n      position: relative;\n    }\n\n    .map-image {\n      width: ".concat(PRINT_MAP_WIDTH, "px;\n    }\n\n    .mapSection {\n      display: flex;\n      border: 1px solid lightgray;\n      margin: 10px 0;\n    }\n\n    .mapSection .datasets{\n      width:200px\n    }\n\n    h1, h2, h3 {\n      clear: both;\n    }\n\n    .WorkbenchItem {\n      padding-bottom: 10px;\n      margin: 0 5px;\n      border-bottom: 1px solid lightgray;\n    }\n\n    .WorkbenchItem:last-of-type {\n      border: 0;\n      padding-bottom: 0;\n    }\n\n    .tjs-_form__input {\n      width: 80%;\n    }\n\n    .tjs-legend__distanceLegend {\n      display: inline-block;\n      text-align: center;\n      position: absolute;\n      bottom: 5px;\n      right: 10px;\n      background: white;\n      padding: 5px;\n    }\n\n    .tjs-legend__distanceLegend > label {\n      color: black;\n    }\n\n    .tjs-legend__distanceLegend:hover {\n      background: #fff;\n    }\n\n    .tjs-legend__bar {\n      border-bottom: 3px solid black;\n      border-right: 3px solid black;\n      border-left: 3px solid black;\n      margin: 0 auto;\n    }\n\n    body {\n      display:flex;\n      justify-content: center;\n      width: 100%\n    }\n\n    @media print {\n      body {\n        display: block;\n      }\n      .PrintView__printControls {\n        display: none;\n      }\n    }\n\n    main {\n      width: 1200px;\n    }\n");
const mkStyle = unsafeCSS => {
  const style = document.createElement("style");
  style.innerHTML = DOMPurify.sanitize(unsafeCSS, {});
  return style;
};
export const downloadImg = function (dataString) {
  let fileName = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : "map.png";
  const a = document.createElement("a");
  a.href = dataString;
  a.download = fileName;
  a.click();
};
const getScale = maybeElement => maybeElement ? PRINT_MAP_WIDTH / maybeElement.offsetWidth : 1;
const PrintView = props => {
  const viewState = useViewState();
  const rootNode = useRef(document.createElement("main"));
  const [screenshot, setScreenshot] = useState(null);
  const [shareLink, setShareLink] = useState("");
  useEffect(() => {
    props.window.document.title = "Print view";
    props.window.document.head.appendChild(mkStyle(styles));
    props.window.document.body.appendChild(rootNode.current);
    props.window.addEventListener("beforeunload", props.closeCallback);
    /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, [props.window]);
  useEffect(() => {
    setScreenshot(viewState.terria.currentViewer.captureScreenshot());
    /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, [props.window]);
  useEffect(() => {
    if (canShorten(viewState.terria)) {
      buildShortShareLink(viewState.terria, viewState, {
        includeStories: false
      }).then(url => {
        setShareLink(url);
      }).catch(() => buildShareLink(viewState.terria, viewState, {
        includeStories: false
      }));
    } else {
      setShareLink(buildShareLink(viewState.terria, viewState, {
        includeStories: false
      }));
    }
  }, [viewState.terria, viewState]);
  return /*#__PURE__*/ReactDOM.createPortal(/*#__PURE__*/_jsx(StyleSheetManager, {
    target: props.window.document.head,
    children: /*#__PURE__*/_jsxs(ThemeProvider, {
      theme: terriaTheme,
      children: [/*#__PURE__*/_jsx(PrintViewButtons, {
        window: props.window,
        screenshot: screenshot
      }), /*#__PURE__*/_jsxs("section", {
        className: "mapSection",
        children: [/*#__PURE__*/_jsx("div", {
          className: "datasets",
          children: /*#__PURE__*/_jsx(PrintWorkbench, {
            workbench: viewState.terria.workbench
          })
        }), /*#__PURE__*/_jsx("div", {
          className: "map",
          children: screenshot ? /*#__PURE__*/_jsx(PrintViewMap, {
            screenshot: screenshot,
            children: /*#__PURE__*/_jsx(DistanceLegend, {
              scale: getScale(viewState.terria.currentViewer.getContainer()),
              isPrintMode: true
            })
          }) : /*#__PURE__*/_jsx("div", {
            children: "Loading..."
          })
        })]
      }), /*#__PURE__*/_jsx("section", {
        className: "PrintView__source",
        children: shareLink && /*#__PURE__*/_jsx(PrintSource, {
          link: shareLink
        })
      }), /*#__PURE__*/_jsxs("section", {
        children: [/*#__PURE__*/_jsx("h2", {
          children: "Datasets"
        }), /*#__PURE__*/_jsx(PrintDatasets, {
          items: viewState.terria.workbench.items
        })]
      })]
    })
  }), rootNode.current);
};
export default PrintView;